import { BoatOfferCard } from '@/components/boat-offer-card';
import { Card } from '@/components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { useBannerVisibilityTracker } from '@/hooks/use-banner-tracking';
import { buildClassifiedPath } from '@/lib/classified-url';
import { getPublicPriceLabel } from '@/lib/currency';
import { getMediaUrl } from '@/lib/media';
import { type Classified, type MediaItem } from '@/types';
import { Link } from '@inertiajs/react';
import { useCallback, useMemo, type MouseEvent } from 'react';

const CLASSIFIEDS_PER_CHUNK = 5;

export interface CarouselInlineBanner {
    id: number;
    title: string;
    link_url: string | null;
    image_alt: string | null;
    desktop_image: MediaItem | null;
    mobile_image: MediaItem | null;
}

interface ClassifiedsInlineBannerCarouselProps {
    classifieds: Classified[];
    banner: CarouselInlineBanner | null;
    favoriteStates: Record<number, boolean>;
    onFavorite: (classifiedId: number) => void;
    onBannerClick: (event: MouseEvent, bannerId: number | null, linkUrl: string | null) => void;
    onBannerVisible?: (bannerId: number, placement: string) => void;
    getPriceLabel?: (classified: Classified) => string;
    getConditionLabel?: (classified: Classified) => string;
    layoutMode?: 'chunked' | 'columns';
    mobileCardVariant?: 'compact' | 'desktop';
    bannerPlacement: string;
}

const chunkClassifieds = (classifieds: Classified[], chunkSize: number): Classified[][] => {
    const chunks: Classified[][] = [];

    for (let index = 0; index < classifieds.length; index += chunkSize) {
        chunks.push(classifieds.slice(index, index + chunkSize));
    }

    return chunks;
};

const getClassifiedLocation = (classified: Classified) => {
    const stateCode = (classified.state as { code?: string } | undefined)?.code;

    if (classified.city?.name && stateCode) {
        return `${classified.city.name}, ${stateCode}`;
    }

    return classified.city?.name || classified.state?.name || '';
};

const getClassifiedImageUrl = (classified: Classified) => {
    return getMediaUrl(classified.main_image, 'card', getMediaUrl(classified.gallery?.[0], 'card', '/boat-1.jpg'));
};

export function ClassifiedsInlineBannerCarousel({
    classifieds,
    banner,
    favoriteStates,
    onFavorite,
    onBannerClick,
    onBannerVisible,
    getPriceLabel,
    getConditionLabel,
    layoutMode = 'chunked',
    mobileCardVariant = 'compact',
    bannerPlacement,
}: ClassifiedsInlineBannerCarouselProps) {
    const desktopChunks = useMemo(() => chunkClassifieds(classifieds, CLASSIFIEDS_PER_CHUNK), [classifieds]);
    const isColumnsLayout = layoutMode === 'columns';
    const useDesktopCardOnMobile = mobileCardVariant === 'desktop';
    const mobileItemClassName = isColumnsLayout ? (useDesktopCardOnMobile ? 'basis-[85%] pl-4 sm:basis-[60%]' : 'basis-64 pl-4') : 'basis-[60%] pl-2';
    const handleBannerVisible = useCallback(
        (bannerId: number) => {
            onBannerVisible?.(bannerId, bannerPlacement);
        },
        [onBannerVisible],
    );
    const mobileBannerRef = useBannerVisibilityTracker<HTMLDivElement>(banner?.id, bannerPlacement, handleBannerVisible);
    const desktopBannerRef = useBannerVisibilityTracker<HTMLDivElement>(banner?.id, bannerPlacement, handleBannerVisible);

    if (desktopChunks.length === 0) {
        return null;
    }

    const firstChunk = desktopChunks[0] ?? [];
    const remainingChunks = desktopChunks.slice(1);
    const remainingClassifieds = remainingChunks.flat();
    const bannerAlt = banner?.image_alt || banner?.title || 'Advertisement';
    const mobileBannerImage = getMediaUrl(banner?.mobile_image, 'banner_mobile', getMediaUrl(banner?.desktop_image, 'banner_desktop', '/ad-1.png'));
    const desktopBannerImage = getMediaUrl(banner?.desktop_image, 'banner_desktop', getMediaUrl(banner?.mobile_image, 'banner_mobile', '/ad-1.png'));

    const resolvePriceLabel = (classified: Classified) => {
        return getPriceLabel
            ? getPriceLabel(classified)
            : getPublicPriceLabel({
                  price: classified.price,
                  priceOnRequest: classified.price_on_request,
              });
    };

    const resolveConditionLabel = (classified: Classified) => {
        return getConditionLabel ? getConditionLabel(classified) : classified.condition === 'new' ? 'Novo' : 'Usado';
    };

    const renderMobileClassifiedCard = (classified: Classified) => {
        if (useDesktopCardOnMobile) {
            return renderDesktopClassifiedCard(classified);
        }

        return (
            <Link
                href={buildClassifiedPath(classified.slug)}
                className="group relative flex h-[198px] w-full cursor-pointer flex-col overflow-hidden rounded-lg bg-white text-left transition-shadow hover:shadow-md focus-visible:ring-2 focus-visible:ring-blue-800 focus-visible:outline-none"
            >
                <div className="absolute top-0 z-10 flex h-12 w-full shrink-0 items-center justify-center rounded-t-lg bg-slate-500/70 px-2">
                    <h3 className="line-clamp-2 text-center text-sm leading-4 font-normal text-white">{classified.title}</h3>
                </div>
                <div className="relative h-full overflow-hidden bg-slate-200">
                    <img
                        src={getClassifiedImageUrl(classified)}
                        alt={classified.title}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover"
                    />
                </div>
                <div className="relative h-12 w-full shrink-0 bg-primary">
                    <div className="absolute right-2 bottom-2 left-2">
                        <div className="p-2">
                            <p className="bg-primary text-center text-base leading-4 font-semibold text-white">Ver oferta</p>
                        </div>
                    </div>
                </div>
            </Link>
        );
    };

    const renderMobileBannerCard = () => {
        if (!banner) {
            return null;
        }

        const mobileBannerHeight = useDesktopCardOnMobile ? 'h-[28rem] sm:h-[25rem]' : 'h-[198px]';

        const bannerImage = (
            <img src={mobileBannerImage} alt={bannerAlt} loading="lazy" decoding="async" className="h-full w-full rounded-lg object-cover" />
        );

        return (
            <div ref={mobileBannerRef} className={`flex w-full cursor-pointer flex-col rounded-lg bg-primary ${mobileBannerHeight}`}>
                {banner.link_url ? (
                    <a
                        href={banner.link_url}
                        onClick={(event) => onBannerClick(event, banner.id, banner.link_url)}
                        className="flex h-full w-full items-center justify-center"
                        aria-label={banner.image_alt || banner.title}
                    >
                        {bannerImage}
                    </a>
                ) : (
                    <div className="flex h-full w-full items-center justify-center">{bannerImage}</div>
                )}
            </div>
        );
    };

    const renderDesktopBannerCard = () => {
        if (!banner) {
            return null;
        }

        const bannerImage = (
            <img src={desktopBannerImage} alt={bannerAlt} loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />
        );

        return (
            <div ref={desktopBannerRef} className="h-[28rem] sm:h-[25rem]">
                <Card className="h-full overflow-hidden rounded-xl bg-primary p-0">
                    {banner.link_url ? (
                        <a
                            href={banner.link_url}
                            onClick={(event) => onBannerClick(event, banner.id, banner.link_url)}
                            className="block h-full w-full cursor-pointer"
                            aria-label={banner.image_alt || banner.title}
                        >
                            {bannerImage}
                        </a>
                    ) : (
                        bannerImage
                    )}
                </Card>
            </div>
        );
    };

    const renderDesktopClassifiedCard = (classified: Classified) => {
        return (
            <BoatOfferCard
                key={classified.id}
                id={classified.id}
                title={classified.title}
                location={getClassifiedLocation(classified)}
                price={resolvePriceLabel(classified)}
                year={classified.year?.toString() ?? ''}
                engine_quantity={classified.engine_quantity ?? 0}
                engine_model={classified.engine_model}
                condition={resolveConditionLabel(classified)}
                imageUrl={getClassifiedImageUrl(classified)}
                isFavorited={favoriteStates[classified.id] ?? classified.is_favorited ?? false}
                isHighlighted={classified.is_highlighted ?? false}
                isCotaNautica={classified.is_cota_nautica ?? false}
                onFavorite={() => onFavorite(classified.id)}
                href={buildClassifiedPath(classified.slug)}
            />
        );
    };

    return (
        <>
            <div className="md:hidden">
                <Carousel
                    opts={{
                        align: 'start',
                        loop: false,
                        containScroll: 'trimSnaps',
                    }}
                    className="w-full"
                >
                    <CarouselContent className={isColumnsLayout ? 'pr-4' : '-ml-2 pr-4'}>
                        {firstChunk.map((classified) => (
                            <CarouselItem key={classified.id} className={mobileItemClassName}>
                                {renderMobileClassifiedCard(classified)}
                            </CarouselItem>
                        ))}

                        {banner && <CarouselItem className={mobileItemClassName}>{renderMobileBannerCard()}</CarouselItem>}

                        {remainingClassifieds.map((classified) => (
                            <CarouselItem key={classified.id} className={mobileItemClassName}>
                                {renderMobileClassifiedCard(classified)}
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                    <CarouselPrevious className="left-0" />
                    <CarouselNext className="right-0" />
                </Carousel>
            </div>

            <div className="hidden md:block">
                <Carousel
                    opts={{
                        align: 'start',
                        loop: false,
                        containScroll: 'trimSnaps',
                    }}
                    className="w-full"
                >
                    {isColumnsLayout ? (
                        <CarouselContent className="pr-4">
                            {firstChunk.map((classified) => (
                                <CarouselItem key={classified.id} className="basis-1/2 pl-4 lg:basis-1/3 xl:basis-1/5">
                                    {renderDesktopClassifiedCard(classified)}
                                </CarouselItem>
                            ))}

                            {banner && <CarouselItem className="basis-1/2 pl-4 lg:basis-1/3 xl:basis-1/5">{renderDesktopBannerCard()}</CarouselItem>}

                            {remainingClassifieds.map((classified) => (
                                <CarouselItem key={classified.id} className="basis-1/2 pl-4 lg:basis-1/3 xl:basis-1/5">
                                    {renderDesktopClassifiedCard(classified)}
                                </CarouselItem>
                            ))}
                        </CarouselContent>
                    ) : (
                        <CarouselContent className="-ml-4 pr-8">
                            <CarouselItem className="basis-[calc(100%-12%)] pl-4">
                                <div className="grid grid-cols-5 gap-6">
                                    {firstChunk.map((classified) => renderDesktopClassifiedCard(classified))}
                                </div>
                            </CarouselItem>

                            {banner && <CarouselItem className="basis-[20%] pl-4">{renderDesktopBannerCard()}</CarouselItem>}

                            {remainingChunks.map((chunk, chunkIndex) => (
                                <CarouselItem key={`desktop-chunk-${chunkIndex}-${chunk[0]?.id ?? 'empty'}`} className="basis-[calc(100%-12%)] pl-4">
                                    <div className="grid grid-cols-5 gap-6">{chunk.map((classified) => renderDesktopClassifiedCard(classified))}</div>
                                </CarouselItem>
                            ))}
                        </CarouselContent>
                    )}
                    <CarouselPrevious className="left-0" />
                    <CarouselNext className="right-0" />
                </Carousel>
            </div>
        </>
    );
}
